import { useEffect, useState } from "react";
import CreatableSelect from "react-select/creatable";
import type { MultiValue, SingleValue } from "react-select";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "react-toastify";
import { postAiConfiguration } from "@/services/setting.service";
import { SettingCheckboxCard } from "@/components/cards/SettingCheckboxCard";
import { BUTTONS, HEADINGS, TOAST_MESSAGES } from "@/utils/constants/appConstants/commonConstants";
import StatusToggle from "@/components/toggles/StatusToggle";
import {
    preventDialogCloseForReactSelect,
    REACT_SELECT_MENU_PORTAL_PROPS,
} from "@/lib/reactSelectPortal";

export interface Field {
    id: string;
    label: string;
    type: string;
    value: string | number | boolean | null;
    setValue: (value: string | number | boolean | null) => void;
    className?: string;
    disabled?: boolean;
}

export interface AiCredentialsModalProps {
    show: boolean;
    setShowModal: (show: boolean) => void;
    refreshSettings: () => void;
    title: string;
    provider: string;
    fields: Field[];
    textModelOptions: { label: string; value: string; type: string }[];
    embeddingModelOptions: { label: string; value: string; type: string }[];
    isMultiSelect?: boolean;
    fetchConfig?: boolean;
    additionalMetaData?: Record<string, any>;
    credentialType: "text" | "embedding";
}

type ModelOption = { label: string; value: string };

export default function UploadAiCredentialModal({
    show,
    setShowModal,
    refreshSettings,
    title,
    provider,
    fields,
    textModelOptions,
    embeddingModelOptions,
    isMultiSelect = true,
    additionalMetaData = {},
    credentialType,
}: AiCredentialsModalProps) {
    const [models, setModels] = useState<ModelOption[]>([]);
    const [markDefault, setMarkDefault] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!show) return;
        setModels([]);
        setMarkDefault(false);
    }, [show, credentialType]);

    const isFormValid = () =>
        fields.every((field) => {
            if (field.type === "checkbox" || field.disabled) return true;
            return field.value !== "" && field.value !== null;
        });

    const handleClose = () => {
        setModels([]);
        setMarkDefault(false);
        setShowModal(false);
    };

    const handleSave = () => {
        setLoading(true);
        const metaData: Record<string, any> = { ...additionalMetaData };
        fields.forEach((field) => {
            if (field.id !== "api-key") metaData[field.id.replace(/-/g, "_")] = field.value;
        });
        const apiKeyField = fields.find((field) => field.id === "api-key");
        const modelsWithoutType = models.map(({ label, value }) => ({ label, value }));

        postAiConfiguration(
            {
                encrypted_api_key: apiKeyField?.value || "",
                provider,
                type: credentialType,
                models: modelsWithoutType,
                is_default: markDefault,
                meta_data: metaData,
            },
            () => {
                setLoading(false);
                fields.forEach((field) => field.setValue(""));
                refreshSettings();
                toast.success(TOAST_MESSAGES.AI_CONFIGURATION_SAVED_SUCCESSFULLY);
                handleClose();
            },
            (err: any) => {
                setLoading(false);
                toast.error(err?.response?.data?.message);
            }
        );
    };

    const isTextMulti = credentialType === "text" && isMultiSelect;
    const modelOptions = credentialType === "text" ? textModelOptions : embeddingModelOptions;
    const modelLabel =
        credentialType === "text" ? `${HEADINGS.MODELS} (Text)` : `${HEADINGS.MODELS} (Embedding)`;
    const modelPlaceholder =
        credentialType === "text" ? "Select text models..." : "Select embedding model...";

    return (
        // modal={false}: Radix removes scroll-lock / outside pointer blocking so portaled
        // react-select menus (on document.body) can receive hover + click.
        <Dialog
            open={show}
            modal={false}
            onOpenChange={(open) => {
                if (!open) handleClose();
            }}
        >
            <DialogContent
                className="gs-upload-ai-credential-dialog"
                onPointerDownOutside={preventDialogCloseForReactSelect}
                onInteractOutside={preventDialogCloseForReactSelect}
                onFocusOutside={preventDialogCloseForReactSelect}
            >
                <div className="gs-upload-ai-credential-dialog__header">
                    <h4 className="gs-upload-ai-credential-dialog__title">{title}</h4>
                </div>

                <div className="gs-upload-ai-credential-dialog__body">
                    {fields.map((field) => (
                        <div key={field.id} className="gs-upload-ai-credential-dialog__field">
                            {field.type === "checkbox" ? (
                                <StatusToggle
                                    name={field.id}
                                    id={field.id}
                                    checked={field.value as boolean}
                                    onChange={(e) => field.setValue(e.target.checked)}
                                >
                                    {field.label}
                                </StatusToggle>
                            ) : (
                                <>
                                    <label className="gs-upload-ai-credential-dialog__label" htmlFor={field.id}>
                                        {field.label}
                                    </label>
                                    <Input
                                        onChange={(e) => field.setValue(e.target.value)}
                                        value={field.value as string | number}
                                        type={field.type}
                                        disabled={field.disabled}
                                        className={field.className}
                                        id={field.id}
                                    />
                                </>
                            )}
                        </div>
                    ))}

                    <div className="gs-upload-ai-credential-dialog__field">
                        <label className="gs-upload-ai-credential-dialog__label" htmlFor={`${provider}-models`}>
                            {modelLabel}
                        </label>
                        <CreatableSelect<ModelOption, boolean>
                            inputId={`${provider}-models`}
                            isMulti={isTextMulti}
                            onChange={(selectedOption) => {
                                if (isTextMulti) {
                                    const selected = (selectedOption as MultiValue<ModelOption>) ?? [];
                                    setModels(
                                        selected.map((item) => ({
                                            label: item.label,
                                            value: item.value,
                                        }))
                                    );
                                    return;
                                }
                                const selected = selectedOption as SingleValue<ModelOption>;
                                setModels(
                                    selected
                                        ? [{ label: selected.label, value: selected.value }]
                                        : []
                                );
                            }}
                            value={isTextMulti ? models : models[0] ?? null}
                            options={modelOptions}
                            className="gs-upload-ai-model-select"
                            classNamePrefix="react-select"
                            isClearable
                            placeholder={modelPlaceholder}
                            closeMenuOnSelect
                            {...REACT_SELECT_MENU_PORTAL_PROPS}
                        />
                    </div>

                    <SettingCheckboxCard
                        className="margin-class"
                        id="mark-default"
                        onChange={(e) => setMarkDefault(e.target.checked)}
                        name="is_default"
                        checked={markDefault}
                    >
                        <p id="setting_is_default_label">{HEADINGS.MARK_AS_DEFAULT}</p>
                    </SettingCheckboxCard>
                </div>

                <div className="gs-upload-ai-credential-dialog__footer">
                    <Button
                        variant="default"
                        className="gs-upload-ai-credential-dialog__submit"
                        disabled={!isFormValid() || loading}
                        onClick={handleSave}
                    >
                        {BUTTONS.SAVE}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
